import { Sprout, CheckCircle, Circle } from 'lucide-react';
import { useDashboard } from '../../../context/DashboardContext';
import useInactivityReturn from '../../../hooks/useInactivityReturn';

/**
 * TodayBlock — "Heute im Garten"
 *
 * State 0 (Onboarding):  Sprout icon, call-to-action button "Garten einrichten"
 *   — falls bereits ein Garten existiert (#456), CTA "Zu deinem Garten" statt
 *     "Garten einrichten", damit ein anon. User nach Reload nicht den Eindruck
 *     bekommt, der Garten sei verloren.
 * State A (Ruhe):        CheckCircle icon, "Heute gibt es nichts zu tun."
 * State B (Relevanz):    Filled circle icon, "Details ansehen →" link
 * State F (#568):         Viele Pflanzen gleichzeitig in einer Pflegephase —
 *   eine ruhige Sammelaussage ("Ordnung statt Liste") mit "Übersicht anzeigen"
 *   statt der Pro-Pflanze-Detailliste. Kein Zählen, kein Aufgaben-Zähler.
 * State G (#569):         Erste Öffnung nach längerer Inaktivität — bei aktiver
 *   Pflegerelevanz (State B oder F) ein wohlwollender Wiedereinstieg statt der
 *   normalen Ansicht. Kein Vorwurf, keine Dringlichkeit. G hat Vorrang vor F.
 */
const TodayBlock = () => {
  const { carePhase, setActiveView, setMobileTab, setModalOpen, setDashboardTab } =
    useDashboard();
  const { state } = carePhase;
  const isReturning = useInactivityReturn();

  // Editor öffnen — Desktop rendert nach activeView, die Mobile-Shell nach
  // mobileTab (App.jsx). Ohne setMobileTab war der CTA auf Mobile wirkungslos;
  // die jeweils andere Shell ignoriert das fremde Feld.
  const openEditor = () => {
    setActiveView('editor');
    setMobileTab?.('editor');
  };

  // #456: After a hard reload, an anon user with a persisted garden but no
  // plants placed yet still lands in State 0. Detect that case so the CTA
  // routes them back to the editor instead of suggesting a fresh setup.
  const hasExistingGarden =
    typeof window !== 'undefined' &&
    !!localStorage.getItem('gartenplaner_active_garden_id');

  return (
    <section className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-base font-bold text-gray-900 mb-4">Heute im Garten</h2>

      {state === '0' && (
        <div>
          <div className="flex items-start gap-3 mb-4">
            <Sprout className="w-12 h-12 text-emerald-500 shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h3 className="text-base font-bold text-gray-900">
                {hasExistingGarden ? 'Dein Garten ist bereit' : 'Dein Grünraum wartet auf dich'}
              </h3>
              <p className="text-[13px] text-gray-600 mt-1">
                {hasExistingGarden
                  ? 'Öffne den Editor, um Pflanzen anzulegen. Sobald Pflege relevant wird, zeigt Grünraum es dir hier.'
                  : 'Mit deinem ersten Garten kannst du Pflanzen anlegen und Grünraum zeigt dir, wann Pflege relevant wird.'}
              </p>
            </div>
          </div>
          <button
            onClick={openEditor}
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-6 py-3 font-medium text-sm"
          >
            {hasExistingGarden ? 'Zu deinem Garten' : 'Garten einrichten'}
          </button>
        </div>
      )}

      {state === 'A' && (
        <div className="flex items-start gap-3">
          <CheckCircle className="w-8 h-8 text-emerald-500 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h3 className="text-base font-bold text-gray-900">Heute gibt es nichts zu tun.</h3>
            <p className="text-[13px] text-gray-600 mt-1">
              Dein Grünraum ist aktuell gut versorgt.
            </p>
          </div>
        </div>
      )}

      {(state === 'B' || state === 'F') && isReturning && (
        <div>
          <div className="flex items-start gap-3 mb-3">
            <Circle
              className="w-6 h-6 text-emerald-400 fill-emerald-400 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <h3 className="text-base font-bold text-gray-900">
              Seit deinem letzten Besuch haben sich neue Pflegephasen ergeben.
            </h3>
          </div>
          <button
            onClick={() => setDashboardTab('pflegekalender')}
            className="text-[13px] text-emerald-600 hover:text-emerald-700 py-2 px-3 -ml-3"
          >
            Überblick anzeigen →
          </button>
        </div>
      )}

      {state === 'F' && !isReturning && (
        <div>
          <div className="flex items-start gap-3 mb-3">
            <Circle
              className="w-6 h-6 text-emerald-400 fill-emerald-400 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <h3 className="text-base font-bold text-gray-900">
              Mehrere Pflanzen befinden sich aktuell in einer Pflegephase.
            </h3>
          </div>
          <button
            onClick={() => setDashboardTab('pflegekalender')}
            className="text-[13px] text-emerald-600 hover:text-emerald-700 py-2 px-3 -ml-3"
          >
            Übersicht anzeigen →
          </button>
        </div>
      )}

      {state === 'B' && !isReturning && (
        <div>
          <div className="flex items-start gap-3 mb-3">
            <Circle
              className="w-6 h-6 text-emerald-400 fill-emerald-400 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <h3 className="text-base font-bold text-gray-900">
              Für einige Pflanzen beginnt jetzt eine Pflegephase.
            </h3>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="text-[13px] text-emerald-600 hover:text-emerald-700 py-2 px-3 -ml-3"
          >
            Details ansehen →
          </button>
        </div>
      )}
    </section>
  );
};

export default TodayBlock;
